import { Fragment, useEffect, useState } from 'react';
import Table from 'shell/components/shared/table';
import { generatePath, useNavigate } from 'react-router-dom';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValues } from '..';
import { useFormik } from 'formik';
import {
  FiltersFormRegisterCertification,
  RegisterCertificationClient,
  RegisterCertificationResponse,
} from 'front-office/view-models/register-certification-list';
import { transformDataset } from 'front-office/utils/register-certification-transform-dataset';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import {
  headCells,
  validationSchemaFilter,
} from 'front-office/constants/register-certification-list';
import ListHeader from 'shell/components/shared/list-header';
import { HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { RegisterCertification } from 'shell/models/register-certification';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { transformRegisterCertificationList } from 'front-office/components/register-certification/utils/transform-register-certification-list';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { authsObj } from 'front-office/constants/register-certification-authorizations';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography, useEventCallback } from '@mui/material';
import { Order } from 'shell/view-models/order';
import FilterRegisterCertification from 'front-office/components/register-certification/list/filter';
import { RegisterCertificationCrumbs } from 'front-office/constants/register-certification-crumbs';

const RegisterCertificationList = (): React.ReactElement => {
  const [registerCertificationList, getRegisterCertificationList] = useHttpFetch<
    HttpResponseList<RegisterCertification>,
    RegisterCertificationResponse
  >(`${__CERTIFICATION_API_URL__}/certificazione/tipi-registro`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformRegisterCertificationList,
  });

  const deleteRegisterCertification = useHttpClient('', {
    options: {
      errorCatching: true,
      method: 'delete',
      loading: true,
      data: {},
    },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<RegisterCertificationClient>>(
    {
      element: null,
      open: false,
    }
  );

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<
    AlertModal<RegisterCertificationClient>
  >({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormRegisterCertification>();

  const onSubmit = useEventCallback((formValues: FiltersFormRegisterCertification) => {
    const queryString = transformDataset({ ...formValues, page: 0 });
    filterForm.setFieldValue('page', 0);
    getRegisterCertificationList({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const filterForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaFilter,
    onSubmit,
  });

  useEffect(() => {
    return () => filterTable.setValues(filterForm.values);
  }, [filterForm.values]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useEventCallback(
    (cell: RegisterCertificationClient): Promise<RegisterCertificationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/ricerca-registro/dettaglio/:id/:institution', {
            id: cell.type,
            institution: cell.institutionCode,
          })
        );
        resolve(cell);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: RegisterCertificationClient): Promise<RegisterCertificationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/ricerca-registro/modifica/:id/:institution', {
            id: cell.type,
            institution: cell.institutionCode,
          })
        );
        resolve(cell);
      });
    }
  );

  const onTrashPress = useEventCallback(
    (cell: RegisterCertificationClient): Promise<RegisterCertificationClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<RegisterCertificationClient>;
    }
  );

  const handleChangePage = useEventCallback((newPage: number) => {
    filterForm.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = { ...filterForm.values, rowsPerPage: rowsPerPage || 10, page: 0 };
    filterForm.setValues(newDataset);
  });

  const onSort = useEventCallback((orders: Order) => {
    const newDataset = { ...filterForm.values, orders };
    const queryString = transformDataset(newDataset);
    (
      getRegisterCertificationList({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<
        HttpResponseList<RegisterCertification>,
        Array<RegisterCertificationClient>
      >
    ).then(() => {
      filterForm.setFieldValue('orders', orders);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={RegisterCertificationCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo registro"
        addBtnLabel="Aggiungi nuovo registro"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sportello/ricerca-registro/nuovo');
        }}
        title="Gestione Ricerca Registro"
      />
      <div className="pmi-my-3">
        <FilterRegisterCertification filterRegisterCertificationForm={filterForm} />
      </div>
      {registerCertificationList.data ? (
        (
          ((registerCertificationList.data as RegisterCertificationResponse)
            ?.list as Array<RegisterCertificationClient>) || []
        ).length ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterForm.values.orders}
            activeActions={auths}
            page={filterForm.values.page}
            rowsPerPage={filterForm.values.rowsPerPage}
            body={
              ((registerCertificationList.data as RegisterCertificationResponse)
                ?.list as Array<RegisterCertificationClient>) || []
            }
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={(registerCertificationList.data as RegisterCertificationResponse)?.count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista ricerca registro"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione ricerca registro"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteRegisterCertification({
            url: `${__CERTIFICATION_API_URL__}/certificazione/tipi-registro/${
              deleteAlertModal.element?.type || ''
            }/ente/${deleteAlertModal.element?.institutionCode}`,
          });
          setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          setDeleteAlertModal({ open: false, element: null });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione ricerca registro"
        message={`La cancellazione della ricerca registro ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterForm.values, page: 0 });
          getRegisterCertificationList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterForm.values, page: 0 });
          getRegisterCertificationList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

RegisterCertificationList.displayName = 'Register Certification List component';

export default RegisterCertificationList;
